import { useRouteMatch } from "react-router";

import {
    SEARCH,
    photosLandingPath,
    SEARCH_ICONS,
    SEARCH_ICON_COLLECTIONS,
    ICON_LANDING_PAGE
} from "data/links";
import {
    heroSearchPlaceholderIcons,
    heroSearchPlaceholderPhotos
} from "./data";

import { desktopHeaderHeight } from "./constants";
import breakpoints from "core/breakpoints";
import colors from "core/colors";

const marginForSearchContainer = desktopHeaderHeight / 2;
const marginTopSearchContainerRemToPx = 27;
const negativeSearchContainerMargin = -(
    marginForSearchContainer - marginTopSearchContainerRemToPx
);

export const animateScrollingDown = ({
    navRef,
    searchContainerRef,
    navLinkRef,
    heroSearchBgColor,
    siteHeaderRef
}) => {
    if (navRef && navRef.current) {
        navRef.current.style.opacity = "0";
        searchContainerRef.current.style.marginTop = `${negativeSearchContainerMargin}px`;
        navLinkRef.current.setAttribute("aria-hidden", "true");
        heroSearchBgColor &&
            (siteHeaderRef.current.style.background = colors.primary.snow);
        siteHeaderRef.current &&
            siteHeaderRef.current.classList.add("scrolledDown");
        siteHeaderRef.current &&
            siteHeaderRef.current.classList.remove("scrolledUp");
    }
};

export const animateScrollingBackUp = ({
    navRef,
    searchContainerRef,
    navLinkRef,
    heroSearchBgColor,
    siteHeaderRef
}) => {
    if (navRef && navRef.current) {
        navRef.current.style.opacity = "1";
        searchContainerRef.current.style.marginTop = "0";
        navLinkRef.current.setAttribute("aria-hidden", "false");
        heroSearchBgColor &&
            (siteHeaderRef.current.style.background = heroSearchBgColor);
        siteHeaderRef.current &&
            siteHeaderRef.current.classList.remove("scrolledDown");
        siteHeaderRef.current &&
            siteHeaderRef.current.classList.add("scrolledUp");
    }
};

// helpers for animations:
import {
    scrollToDesktopWithHero,
    scrollToTabletWithHero,
    scrollToMobileWithHero,
    scrollToMobileWithHeroIcons,
    scrollToDesktopNoHero,
    scrollToDesktopHeroSearchBar,
    scrollToTabletHeroSearchBar,
    scrollToMobileHeroSearchBar,
    scrollToMobileHeroSearchBarIcons,
    scrollToDesktopWithHeroLanding,
    scrollToTabletWithHeroLanding,
    scrollToMobileWithHeroLanding,
    scrollToMobileWithHeroIconsLanding,
    scrollToDesktopHeroLandingSearchBar,
    scrollToTabletHeroLandingSearchBar,
    scrollToMobileHeroLandingSearchBar,
    scrollToMobileHeroLandingIconsSearchBar
} from "./constants";

const tabletBreakpoint = breakpoints.beforeTablet.px;
const desktopBreakpoint = breakpoints.beforeDesktop.px;

export const scrollPosAnimation = ({ browserWidth, isHeroSearchV2 }) => {
    const isPhotosLanding =
        typeof window !== "undefined" &&
        window.location.pathname === photosLandingPath;
    const isIconsLanding =
        typeof window !== "undefined" &&
        window.location.pathname === ICON_LANDING_PAGE;

    const isLandingPage = isPhotosLanding || isIconsLanding;

    let animateHeaderScrollPos;

    if (isLandingPage) {
        if (isPhotosLanding) {
            if (browserWidth > desktopBreakpoint) {
                // desktop scroll position:
                animateHeaderScrollPos = scrollToDesktopWithHeroLanding;
            } else if (browserWidth > tabletBreakpoint) {
                // tablet scroll position:
                animateHeaderScrollPos = scrollToTabletWithHeroLanding;
            } else {
                // mobile scroll position:
                animateHeaderScrollPos = scrollToMobileWithHeroLanding;
            }
        }

        if (isIconsLanding) {
            if (browserWidth > desktopBreakpoint) {
                // desktop scroll position:
                animateHeaderScrollPos = scrollToDesktopWithHeroLanding;
            } else if (browserWidth > tabletBreakpoint) {
                // tablet scroll position:
                animateHeaderScrollPos = scrollToTabletWithHeroLanding;
            } else {
                // mobile scroll position:
                animateHeaderScrollPos = scrollToMobileWithHeroIconsLanding;
            }
        }
    } else {
        if (browserWidth > desktopBreakpoint) {
            // desktop scroll position:
            animateHeaderScrollPos = scrollToDesktopWithHero;
        } else if (browserWidth > tabletBreakpoint) {
            // tablet scroll position:
            animateHeaderScrollPos = scrollToTabletWithHero;
        } else {
            // mobile scroll position:
            animateHeaderScrollPos = scrollToMobileWithHero;
        }
    }

    return isHeroSearchV2 ? animateHeaderScrollPos : scrollToDesktopNoHero;
};

export const scrollPosShowHideSearch = ({ browserWidth, isHeroSearchV2 }) => {
    const isPhotosLanding =
        typeof window !== "undefined" &&
        window.location.pathname === photosLandingPath;
    const isIconsLanding =
        typeof window !== "undefined" &&
        window.location.pathname === ICON_LANDING_PAGE;

    const isLandingPage = isPhotosLanding || isIconsLanding;

    let showHideHeroScrollPos;

    if (isLandingPage) {
        if (isPhotosLanding) {
            if (browserWidth > desktopBreakpoint) {
                // desktop scroll position:
                showHideHeroScrollPos = scrollToDesktopHeroLandingSearchBar;
            } else if (browserWidth > tabletBreakpoint) {
                // tablet scroll position:
                showHideHeroScrollPos = scrollToTabletHeroLandingSearchBar;
            } else {
                // mobile scroll position:
                showHideHeroScrollPos = scrollToMobileHeroLandingSearchBar;
            }
        }

        if (isIconsLanding) {
            if (browserWidth > desktopBreakpoint) {
                // desktop scroll position:
                showHideHeroScrollPos = scrollToDesktopHeroLandingSearchBar;
            } else if (browserWidth > tabletBreakpoint) {
                // tablet scroll position:
                showHideHeroScrollPos = scrollToTabletHeroLandingSearchBar;
            } else {
                // mobile scroll position:
                showHideHeroScrollPos = scrollToMobileHeroLandingIconsSearchBar;
            }
        }
    } else {
        if (browserWidth > desktopBreakpoint) {
            // desktop scroll position:
            showHideHeroScrollPos = scrollToDesktopHeroSearchBar;
        } else if (browserWidth > tabletBreakpoint) {
            // tablet scroll position:
            showHideHeroScrollPos = scrollToTabletHeroSearchBar;
        } else {
            // mobile scroll position:
            showHideHeroScrollPos = scrollToMobileHeroSearchBar;
        }
    }

    return isHeroSearchV2 ? showHideHeroScrollPos : 0;
};

//  when scrolling down the page, hide hero search & show header search
export const scrollSearchBarScrollingDown = ({
    heroSearchWrapper,
    isHeroSearchV2,
    searchWrapperRef
}) => {
    if (searchWrapperRef && searchWrapperRef.current) {
        isHeroSearchV2 &&
            searchWrapperRef.current.classList.add("scrolledDownSearch");
        isHeroSearchV2 &&
            searchWrapperRef.current.classList.remove("scrolledUpSearch");
    }

    if (heroSearchWrapper) {
        isHeroSearchV2 && (heroSearchWrapper.style.display = "none");
    }
};

// when scrolling up the page, show hero search & hide header search
export const scrollSearchBarScrollingUp = ({
    heroSearchWrapper,
    isHeroSearchV2,
    searchWrapperRef
}) => {
    if (searchWrapperRef && searchWrapperRef.current) {
        isHeroSearchV2 &&
            searchWrapperRef.current.classList.remove("scrolledDownSearch");
        isHeroSearchV2 &&
            searchWrapperRef.current.classList.add("scrolledUpSearch");
    }

    if (heroSearchWrapper) {
        isHeroSearchV2 && (heroSearchWrapper.style.display = "block");
    }
};

// for search input placeholder text:

export const placeholderText = ({ browserWidth, isPhotos = false }) => {
    let placeholder;

    const placeholderType = isPhotos
        ? heroSearchPlaceholderPhotos
        : heroSearchPlaceholderIcons;

    if (browserWidth > breakpoints.beforeDesktop.px) {
        // desktop hero search placeholder:
        placeholder = placeholderType.desktop;
    } else if (browserWidth > breakpoints.beforeTablet.px) {
        // tablet hero search placeholder:
        placeholder = placeholderType.tablet;
    } else {
        // mobile hero search placeholder:
        placeholder = placeholderType.mobile;
    }

    return placeholder;
};

export const searchURLPath = ({ currentPath, searchType }) => {
    let path;

    if (searchType === "Photos") {
        // ^^ if selected toggle button === photos:
        path = SEARCH;
    } else if (searchType === "Icons") {
        // ^^ if selected toggle button === icons:

        if (currentPath === SEARCH_ICON_COLLECTIONS) {
            // ^^ if the page user is on is collection page:
            path = SEARCH_ICON_COLLECTIONS;
        } else {
            path = SEARCH_ICONS;
        }
    } else {
        throw new Error(`Unknown search type "${searchType}"`);
    }

    return path;
};
